import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError } from 'rxjs/operators';
import { firstValueFrom, throwError } from 'rxjs';
import { StorageService } from '../services/storage.service';
import { DialogService } from '../services/dialog.service';
import { AuthStateService } from '../services/auth-state.service';
import { TenantService } from '../services/tenant.service';
import { MenuService } from '../services/menu.service';
import {
  GUEST_PI_STORAGE_KEY,
  GUEST_UID_STORAGE_KEY,
} from '../services/guest-firebase-auth.service';
import { SessionExpiredDialogComponent } from '../components/session-expired-dialog/session-expired-dialog.component';
import { clearRescheduleAppointmentSession } from '../utils/reschedule-cancel-policy.util';

const signInReturnShortNameKey = 'sign_in_return_short_name';
const tenantShortNameSessionKey = 'tenant_short_name';

export const sessionInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const dialogService = inject(DialogService);
  const authStateService = inject(AuthStateService);
  const tenantService = inject(TenantService);
  const menuService = inject(MenuService);
  const router = inject(Router);

  // Login SessionId: pending MFA (pre user_info) or logged-in user
  const userInfo = storageService.getSessionStorage('user_info');
  const pendingMfa = storageService.getSessionStorage('pending_mfa_login');
  const sessionId = userInfo?.sessionId || pendingMfa?.userInfo?.sessionId
    || sessionStorage.getItem('__session_id__');

  if (sessionId) {
    req = req.clone({
      setHeaders: {
        'sessionid': sessionId
      }
    });
  }

  return next(req).pipe(
    tap({
      next: (event) => {
        // Check if it's an HttpResponse and has a body
        if (event instanceof HttpResponse && event.body) {
          const body = event.body as any;

          if (isPortalSendIPRequest(req.url)) {
            return;
          }

          // If session expires when the user is on the MFA page. show the session expired dialog and redirect to login page after user clicks OK.
          if (isPatientMfaRequest(req.url) && !isSessionInvalidResponse(body)) {
            return;
          }

          // Check if the response indicates session validation failure
          if (isSessionInvalidResponse(body)) {
            // Handle session expiration
            handleSessionExpiration(storageService, authStateService, dialogService, tenantService, menuService, router);
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        // Also check error responses for session invalidation
        if (error && !isPortalSendIPRequest(req.url) && isSessionInvalidError(error)) {
          handleSessionExpiration(storageService, authStateService, dialogService, tenantService, menuService, router);
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Check error responses for session invalidation
      if (!isPortalSendIPRequest(req.url) && isSessionInvalidError(error)) {
        handleSessionExpiration(storageService, authStateService, dialogService, tenantService, menuService, router);
      }
      return throwError(() => error);
    })
  );
};

function isPatientMfaRequest(url: string): boolean {
  return url.includes('/auth/patient-mfa/');
}

/** Audit log on login — failure must not log the user out (same as normal login fire-and-forget). */
function isPortalSendIPRequest(url: string): boolean {
  return url.includes('/auth/PortalSendIP');
}

/**
 * Checks if a response body indicates session invalidation
 * Handles responses like:
 * - { "success": "false", "message": "...Invalid session" }
 * - { "success": false, "message": "...Invalid session" }
 * - { "message": "...Invalid session" }
 * - { "descriptionField": "Invalid session" }
 * - Messages with nested error text like "Failed to communicate with .NET Core service: ... Invalid session"
 */
function isSessionInvalidResponse(body: any): boolean {
  if (!body) {
    return false;
  }

  // Helper function to check if a string contains session invalidation patterns
  const checkSessionInvalid = (text: string): boolean => {
    if (!text || typeof text !== 'string') {
      return false;
    }
    const textLower = text.toLowerCase();
    if (isMfaOtpFailureMessage(textLower)) {
      return false;
    }
    return (
      textLower.includes('session validation failed') ||
      textLower.includes('invalid sessionid') ||
      textLower.includes('invalid session') ||
      textLower.includes('session invalid')
    );
  };

  // Get message from various possible fields
  const message = body.message || body.error || body.errorMessage || body.descriptionField || '';
  
  // Check the message directly
  if (checkSessionInvalid(message)) {
    return true;
  }

  // Also check if success is explicitly false (string or boolean)
  // This catches cases where success: "false" is set but message might be in different format
  if (body.success === 'false' || body.success === false) {
    const msg = body.message || body.error || body.errorMessage || body.descriptionField || '';
    if (checkSessionInvalid(msg)) {
      return true;
    }
  }

  // Check nested error objects or arrays
  if (body.error && typeof body.error === 'object') {
    if (checkSessionInvalid(JSON.stringify(body.error))) {
      return true;
    }
  }

  // Check if the entire body as string contains session invalidation
  // This catches deeply nested error messages
  try {
    const bodyString = JSON.stringify(body).toLowerCase();
    if (isMfaOtpFailureMessage(bodyString)) {
      return false;
    }
    if (
      bodyString.includes('invalid session') ||
      bodyString.includes('session invalid') ||
      bodyString.includes('session validation failed') ||
      bodyString.includes('invalid sessionid')
    ) {
      if (checkSessionInvalid(message) || checkSessionInvalid(body.error)) {
        return true;
      }
    }
  } catch (e) {
    // If stringify fails, ignore
  }

  return false;
}

function isMfaOtpFailureMessage(textLower: string): boolean {
  return (
    textLower.includes('invalid otp') ||
    textLower.includes('otp expired') ||
    textLower.includes('otp is required') ||
    textLower.includes('maximum otp limit')
  );
}

/**
 * Checks if an HTTP error response indicates session invalidation
 */
export function isSessionInvalidError(error: HttpErrorResponse): boolean {
  if (!error) {
    return false;
  }

  // Check error.error (which is the parsed error body)
  if (error.error) {
    if (typeof error.error === 'string') {
      const errorLower = error.error.toLowerCase();
      if (isMfaOtpFailureMessage(errorLower)) {
        return false;
      }
      if (
        errorLower.includes('session validation failed') ||
        errorLower.includes('invalid sessionid') ||
        errorLower.includes('invalid session') ||
        errorLower.includes('session invalid')
      ) {
        return true;
      }
    } else if (typeof error.error === 'object') {
      // Use the same robust checking function
      return isSessionInvalidResponse(error.error);
    }
  }

  // Check error.message
  if (error.message) {
    const messageLower = error.message.toLowerCase();
    if (isMfaOtpFailureMessage(messageLower)) {
      return false;
    }
    if (
      messageLower.includes('session validation failed') ||
      messageLower.includes('invalid sessionid') ||
      messageLower.includes('invalid session') ||
      messageLower.includes('session invalid')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Handles session expiration by clearing data and redirecting to login
 */
function handleSessionExpiration(
  storageService: StorageService,
  authStateService: AuthStateService,
  dialogService: DialogService,
  tenantService: TenantService,
  menuService: MenuService,
  router: Router
): void {
  if (!dialogService.beginSessionExpiration()) {
    return;
  }

  const pendingMfa = storageService.getSessionStorage('pending_mfa_login');
  const tenantCode =
    storageService.getSessionStorage('tenant_code') || pendingMfa?.tenantCode || null;
  const tenantShortName = resolveSignInShortName(tenantService, pendingMfa);

  clearPortalSessionForExpiration(storageService, authStateService, tenantService, menuService);

  dialogService.open(SessionExpiredDialogComponent).then(() => {
    dialogService.endSessionExpiration();
    navigateToSignInAfterExpiration(router, tenantService, tenantShortName, tenantCode);
  });
}

/** Reads all known sign-in redirect hints before session data is cleared. */
function resolveSignInShortName(
  tenantService: TenantService,
  pendingMfa: { currentTenant?: { tenantShortName?: string } } | null
): string | null {
  const fromReturn = sessionStorage.getItem(signInReturnShortNameKey)?.trim();
  if (fromReturn) {
    return fromReturn;
  }

  const fromShort = sessionStorage.getItem(tenantShortNameSessionKey)?.trim();
  if (fromShort) {
    return fromShort;
  }

  const fromPending = pendingMfa?.currentTenant?.tenantShortName?.trim();
  if (fromPending) {
    return fromPending;
  }

  return tenantService.getCurrentTenant()?.tenantShortName?.trim() || null;
}

/** Mirrors AuthService.clearPortalSession — keeps sign_in_return_short_name for redirect fallback. */
function clearPortalSessionForExpiration(
  storageService: StorageService,
  authStateService: AuthStateService,
  tenantService: TenantService,
  menuService: MenuService
): void {
  storageService.removeSessionStorageByKey('user_info');
  storageService.removeSessionStorageByKey('tenant_code');
  storageService.removeSessionStorageByKey('tenant_id');
  storageService.removeSessionStorageByKey('user_permissions');
  storageService.removeSessionStorageByKey('user_icon_settings');
  storageService.removeSessionStorageByKey('user_other_settings');
  storageService.removeSessionStorageByKey('associated_patients');
  storageService.removeSessionStorageByKey('pending_mfa_login');
  storageService.removeSessionStorageByKey('pending_mfa_audit_events');
  storageService.removeSessionStorageByKey('mfa_pending_otp_verify');
  storageService.removeSessionStorageByKey('has_seen_welcome_modal');
  storageService.removeSessionStorageByKey('mfa_reminder_dismissed_session');
  storageService.removeSessionStorageByKey('is_quick_activated');
  storageService.removeSessionStorageByKey('tenant_name');
  sessionStorage.removeItem(tenantShortNameSessionKey);
  sessionStorage.removeItem('tenant_type');
  // PE-6231 — drop original appointment cancel payload on session expiry
  clearRescheduleAppointmentSession();
  storageService.clearSessionId();
  // Clear `dm_email` / `_grecaptcha`
  storageService.clearLoggedOutBrowserKeys();
  // Guest booking keys must not survive session expiry
  storageService.removeSessionStorageByKey(GUEST_PI_STORAGE_KEY);
  storageService.removeSessionStorageByKey(GUEST_UID_STORAGE_KEY);
  authStateService.logout();
  tenantService.clearCurrentTenant();
  menuService.resetMenuState();
}

function navigateToSignInAfterExpiration(
  router: Router,
  tenantService: TenantService,
  tenantShortName: string | null,
  tenantCode: string | null
): void {
  const shortName =
    tenantShortName || sessionStorage.getItem(signInReturnShortNameKey)?.trim() || null;

  if (shortName) {
    router.navigate([`/${shortName}`], { queryParams: { sessionEnded: '1' } });
    return;
  }

  if (tenantCode) {
    firstValueFrom(tenantService.getTenantByCode(tenantCode)).then(tenant => {
      if (tenant?.tenantShortName) {
        router.navigate([`/${tenant.tenantShortName}`], { queryParams: { sessionEnded: '1' } });
      } else {
        router.navigate(['/page-not-found']);
      }
    }).catch(() => {
      router.navigate(['/page-not-found']);
    });
    return;
  }

  router.navigate(['/page-not-found']);
}